import { useDroppable } from '@dnd-kit/core';
import { ChevronRight } from '@/design-system/icons';
import { cn } from '@/lib';
import { ROOT_DROP_ID } from '../document-explorer/useDragAndDrop';
import type { DocumentBreadcrumbItem } from '../../types';

interface DocumentBreadcrumbProps {
  rootLabel: string;
  items: DocumentBreadcrumbItem[];
  onRootClick: () => void;
  onItemClick: (item: DocumentBreadcrumbItem, index: number) => void;
  /** When true the crumbs act as drop targets (admin dragging an item). */
  isDndActive?: boolean;
}

/** Droppable wrapper for a single breadcrumb crumb. */
function DroppableCrumb({
  droppableId,
  isDndActive,
  children,
}: {
  droppableId: string;
  isDndActive: boolean;
  children: (isOver: boolean) => React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  return (
    <span ref={setNodeRef} className={cn('rounded', isDndActive && isOver && 'ring-2 ring-primary ring-offset-1')}>
      {children(isOver)}
    </span>
  );
}

/**
 * Renders the breadcrumb trail below the document explorer.
 * When `isDndActive` is true, every crumb (including root) becomes a drop
 * target so users can drag a file or folder directly to a parent level.
 */
export function DocumentBreadcrumb({
  rootLabel,
  items,
  onRootClick,
  onItemClick,
  isDndActive = false,
}: DocumentBreadcrumbProps) {
  return (
    <nav aria-label={rootLabel} className="min-w-0 relative">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-onsurface/70">
        <li>
          <DroppableCrumb droppableId={ROOT_DROP_ID} isDndActive={isDndActive}>
            {(isOver) =>
              items.length === 0 ? (
                <span className={cn('font-medium text-onsurface', isDndActive && isOver && 'text-primary')}>
                  {rootLabel}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={onRootClick}
                  className={cn(
                    'rounded-sm text-left transition-colors hover:text-primary focus:outline-none focus:text-primary',
                    isDndActive && isOver && 'text-primary'
                  )}
                >
                  {rootLabel}
                </button>
              )
            }
          </DroppableCrumb>
        </li>

        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li key={item.id} className="flex min-w-0 items-center gap-2">
              <ChevronRight className="size-4 shrink-0 text-onsurface/40" />
              <DroppableCrumb droppableId={item.id} isDndActive={isDndActive}>
                {(isOver) =>
                  isLast ? (
                    <span className={cn('truncate font-medium text-onsurface', isDndActive && isOver && 'text-primary')}>
                      {item.name}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onItemClick(item, index)}
                      className={cn(
                        'truncate rounded-sm text-left transition-colors hover:text-primary focus:outline-none focus:text-primary',
                        isDndActive && isOver && 'text-primary'
                      )}
                    >
                      {item.name}
                    </button>
                  )
                }
              </DroppableCrumb>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
